<div class="dialog-container">
    <!-- Dialog Header -->
    <div class="dialog-header d-flex justify-content-between align-items-center mb-4">
        <h4 class="dialog-title mb-0">Admin Meeting - {{ meetingTitle }}</h4>
        <button type="button" class="close-btn" (click)="minimizeDialog()" aria-label="Minimize dialog" title="Minimize">
            <i class="bi bi-dash-lg"></i>
        </button>
    </div>

    <!-- Back to agenda list link -->
    <div class="mb-3" style="padding: 0 20px;">
        <a href="#" class="back-link" (click)="goBackToAgendaList(); $event.preventDefault()">
            <i class="fas fa-arrow-left me-2"></i> Back to agenda list
        </a>
    </div>

    <!-- Dialog Content -->
    <form [formGroup]="decisionForm" *ngIf="currentAgendaItem" class="dialog-form">
        <div class="dialog-content-scrollable">
        <!-- Who is Attending Section -->
        <div class="attending-section mb-3">
            <h5 class="section-title fw-bold mb-2">
                <i class="fas fa-users me-2"></i>Who is Attending
            </h5>
            <div class="attendees-list">
                <div *ngIf="attendees.length === 0" class="text-muted">
                    No attendees yet
                </div>
                <div *ngFor="let attendee of attendees" class="attendee-item d-flex align-items-center justify-content-between">
                    <div class="d-flex align-items-center">
                        <i class="fas fa-user-circle me-2"></i>
                        <div class="d-flex flex-column">
                            <span class="fw-semibold">{{ attendee.userName || 'User' }}</span>
                            <small class="text-muted">{{ formatRole(getAttendeeRole(attendee.userId)) }}</small>
                        </div>
                        <!-- Show answer status for current agenda item -->
                        <span *ngIf="currentAgendaMembersAnswers.length > 0" class="ms-2">
                            <span *ngFor="let answer of currentAgendaMembersAnswers">
                                <span *ngIf="answer.userId === attendee.userId" class="badge ms-2" 
                                      [class.bg-success]="answer.action === 'accept'"
                                      [class.bg-danger]="answer.action === 'reject'"
                                      [class.bg-warning]="answer.action === 'defer'">
                                    <i class="fas" 
                                       [class.fa-check]="answer.action === 'accept'"
                                       [class.fa-times]="answer.action === 'reject'"
                                       [class.fa-clock]="answer.action === 'defer'"
                                       [class.me-1]="true"></i>
                                    {{ answer.action === 'accept' ? 'Accepted' : (answer.action === 'reject' ? 'Rejected' : 'Reserved') }}
                                </span>
                            </span>
                        </span>
                    </div>
                    <!-- Remove button (only show for non-hoster attendees) -->
                    <button 
                        *ngIf="attendee.userId !== currentUserId"
                        type="button" 
                        class="btn btn-sm btn-link text-danger p-0 ms-2"
                        (click)="kickUser(attendee.userId, attendee.userName)"
                        [title]="'Remove ' + attendee.userName + ' from meeting'"
                        style="min-width: 24px; height: 24px; line-height: 1;">
                        <i class="fas fa-times"></i>
                    </button>
                </div>
            </div>
            <!-- Summary of answers for current agenda -->
            <div *ngIf="currentAgendaMembersAnswers.length > 0" class="answers-summary mt-2 p-2 bg-light rounded">
                <small class="text-muted">
                    <i class="fas fa-info-circle me-1"></i>
                    Current Agenda: 
                    <span class="text-success ms-2">
                        <i class="fas fa-check me-1"></i>{{ acceptedCount }} Accepted
                    </span>
                    <span class="text-danger ms-2">
                        <i class="fas fa-times me-1"></i>{{ rejectedCount }} Rejected
                    </span>
                    <span class="text-warning ms-2">
                        <i class="fas fa-clock me-1"></i>{{ deferredCount }} Reserved
                    </span>
                </small>
            </div>
        </div>

        <!-- Agenda Items Navigation -->
        <div class="agenda-navigation mb-3" *ngIf="agendaItems.length > 1">
            <div class="agenda-items-list" #agendaItemsList>
                <div 
                    *ngFor="let item of agendaItems; let i = index" 
                    class="agenda-item-tab"
                    [class.active]="i === currentAgendaIndex"
                    [class.disabled]="(i > currentAgendaIndex + 1) || (i === currentAgendaIndex + 1 && !canMoveForwardFromCurrentAgenda)"
                    (click)="selectAgendaItem(i, true)"
                    [style.cursor]="((i > currentAgendaIndex + 1) || (i === currentAgendaIndex + 1 && !canMoveForwardFromCurrentAgenda)) ? 'not-allowed' : 'pointer'"
                    [title]="(i > currentAgendaIndex + 1) ? 'Cannot skip ahead to agenda item ' + (i + 1) : (i === currentAgendaIndex + 1 && !canMoveForwardFromCurrentAgenda) ? getForwardNavigationBlockedMessage('navigate forward') : 'Click to navigate to agenda item ' + (i + 1)">
                    <span class="agenda-tab-number">{{ item.order || (i + 1) }}</span>
                    <span class="agenda-tab-subject">{{ item.subject }}</span><span class="agenda-stage-pill" [ngClass]="getAgendaStageClass(item.currentStage)" *ngIf="item.currentStage">{{ item.currentStage }}</span>
                </div>
            </div>
            <!-- Warning message if not all voting members have answered -->
            <div *ngIf="!allMembersAnswered && tcmMembers.length > 0" class="alert alert-warning mt-2 mb-0">
                <i class="fas fa-exclamation-triangle me-2"></i>
                <strong>Warning:</strong> 
                <span *ngIf="!hasChairmanOrViceChairmanAnswered">
                    At least one Chairman or Vice Chairman must provide their answer before proceeding.
                </span>
                <span *ngIf="hasChairmanOrViceChairmanAnswered">
                    {{ unansweredCount }} committee member(s) have not provided their answer yet. You cannot navigate forward until all voting members respond.
                </span>
            </div>
        </div>

        <!-- Agenda Title Section -->
        <div class="form-section">
            <h5 class="section-title fw-bold">Agenda Title</h5>
            <div class="agenda-title-box">
                <div class="agenda-subject fw-bold d-flex align-items-center gap-2 flex-wrap">{{ currentAgendaItem?.subject || 'N/A' }}<span class="agenda-stage-pill" [ngClass]="getAgendaStageClass(currentAgendaItem?.currentStage)" *ngIf="currentAgendaItem?.currentStage">{{ currentAgendaItem?.currentStage }}</span></div>
                <div class="agenda-datetime text-muted mt-2">{{ currentAgendaItem?.dateTime || 'N/A' }}</div>
            </div>
        </div>

        <!-- Agenda Description Section -->
        <div class="form-section">
            <h5 class="section-title fw-bold">Agenda Description</h5>
            <div class="row">
                <div class="col-12 col-md-6 mb-3 mb-md-0">
                    <div class="description-column">
                        <div class="description-label fw-bold mb-2">English</div>
                        <div class="description-content p-3">
                            {{ getAgendaDescriptionEN() || 'No English description available.' }}
                        </div>
                    </div>
                </div>
                <div class="col-12 col-md-6">
                    <div class="description-column">
                        <div class="description-label fw-bold mb-2">العربية (Arabic)</div>
                        <div class="description-content p-3">
                            {{ getAgendaDescriptionAR() || 'لا يوجد وصف بالعربية.' }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Required from Tender Committee Section -->
        <div class="form-section">
            <h5 class="section-title fw-bold">Required from Tender Committee</h5>
            <textarea 
                class="form-control" 
                rows="4"
                [readonly]="true"
                [value]="currentAgendaItem?.requiredFromTC || ''"
                placeholder="No requirements specified...">
            </textarea>
        </div>

        <!-- Attachments Section -->
        <div class="form-section" *ngIf="currentAgendaItemAttachments.length > 0">
            <h5 class="section-title fw-bold mb-3">
                <i class="fas fa-paperclip me-2"></i>Attachments
            </h5>
            <div class="attachments-list">
                <div *ngFor="let attachment of currentAgendaItemAttachments" class="attachment-item d-flex align-items-center p-3 mb-2 rounded">
                    <div class="attachment-icon me-3 flex-shrink-0">
                        <i class="fas fa-file-pdf text-danger" *ngIf="attachment.fileType === 'application/pdf'"></i>
                        <i class="fas fa-file-word text-primary" *ngIf="attachment.fileType?.includes('word')"></i>
                        <i class="fas fa-file-image text-info" *ngIf="attachment.fileType?.includes('image')"></i>
                        <i class="fas fa-file text-secondary" *ngIf="!attachment.fileType?.includes('pdf') && !attachment.fileType?.includes('word') && !attachment.fileType?.includes('image')"></i>
                    </div>
                    <div class="attachment-info flex-grow-1 min-w-0">
                        <div class="attachment-name fw-bold" [title]="attachment.originalName || attachment.name">{{ attachment.originalName || attachment.name }}</div>
                        <div class="attachment-size text-muted small">{{ attachment.size }}</div>
                    </div>
                    <div class="attachment-actions flex-shrink-0 ms-3">
                        <button 
                            type="button"
                            class="btn btn-sm btn-outline-primary me-2"
                            (click)="downloadAttachment(attachment); $event.stopPropagation()">
                            <i class="fas fa-download me-1"></i>Download
                        </button>
                        <button 
                            type="button" 
                            class="btn btn-sm btn-outline-secondary"
                            (click)="previewAttachment(attachment); $event.stopPropagation()"
                            title="Preview">
                            <i class="fas fa-eye me-1"></i>Preview
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <div class="form-section" *ngIf="currentAgendaItemAttachments.length === 0 && isLoadingAttachments === false">
            <h5 class="section-title fw-bold mb-3">
                <i class="fas fa-paperclip me-2"></i>Attachments
            </h5>
            <div class="text-muted p-3 bg-light rounded">
                <i class="fas fa-info-circle me-2"></i>No attachments available for this agenda item.
            </div>
        </div>

        <!-- TC Notes -->
        <div class="form-section">
            <h5 class="section-title fw-bold">TC Notes</h5>
            <div class="row">
                <div class="col-12 mb-3 mb-md-0">
                    <div class="description-column">
                        <div class="description-label fw-bold mb-2">Notes</div>
                        <div class="description-content p-3">
                            {{ currentAgendaItem?.tcNotes || 'No TC Notes available.' }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Bid Unsealing Section -->
        <div class="form-section" *ngIf="shouldShowUnsealSection">
            <h5 class="section-title fw-bold">{{ unsealSectionTitle }}</h5>
            <div *ngIf="isLoadingUnsealBids" class="text-muted p-3 bg-light rounded">
                <i class="fas fa-spinner fa-spin me-2"></i>Loading related bids...
            </div>
            <div *ngIf="!isLoadingUnsealBids && unsealBids.length === 0" class="text-muted p-3 bg-light rounded">
                <i class="fas fa-info-circle me-2"></i>No related bids found for this agenda item.
            </div>
            <div class="table-responsive" *ngIf="!isLoadingUnsealBids && unsealBids.length > 0">
                <table class="table unseal-bids-table mb-0">
                    <thead>
                        <tr>
                            <th>Bid ID</th>
                            <th>Vendor</th>
                            <th>Bid Amount</th>
                            <th>Bid Valid Till</th>
                            <th>Documents</th>
                            <th>Member Status</th>
                            <th class="text-end">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr *ngFor="let bid of unsealBids" [class.unsealed-row]="isBidUnsealedForScope(bid)">
                            <td class="fw-bold">{{ bid.bidId }}</td>
                            <td>{{ bid.vendorName || bid.vendorId || '-' }}</td>
                            <td>{{ formatBidAmount(bid.bidAmount) }}</td>
                            <td>{{ formatBidDate(bid.bidValidTill) }}</td>
                            <td>
                                <ng-container *ngIf="getVisibleUnsealDocuments(bid).length > 0; else sealedDocumentState">
                                    <div class="unseal-doc-link" *ngFor="let document of getVisibleUnsealDocuments(bid)">
                                        <i class="fas fa-file-alt me-1"></i>
                                        <button type="button" class="unseal-link-button" (click)="openUnsealDocument(document)" [disabled]="isUnsealDocumentDownloading(document)">
                                            <ng-container *ngIf="isUnsealDocumentDownloading(document); else unsealDocumentName">
                                                <i class="fas fa-spinner fa-spin me-1"></i>Downloading...
                                            </ng-container>
                                            <ng-template #unsealDocumentName>{{ document.name || document.attachmentFor || 'Document' }}</ng-template>
                                        </button>
                                        <span class="text-muted small" *ngIf="formatUnsealFileSize(document.fileSize)">({{ formatUnsealFileSize(document.fileSize) }})</span>
                                    </div>
                                </ng-container>
                                <ng-template #sealedDocumentState>
                                    <div>{{ getUnsealDocumentSummary(bid) }}</div>
                                    <span class="unseal-status" [class.unsealed]="isBidUnsealedForScope(bid)">{{ getScopeStatusText(bid) }}</span>
                                </ng-template>
                                <button type="button" class="btn btn-sm btn-outline-secondary mt-2" (click)="openBidDocumentsDialog(bid)"><i class="fas fa-folder-open me-1"></i>Bid documents</button>
                            </td>
                            <td>
                                <ng-container *ngIf="isCommercialUnsealBlockedByTechnicalRejection(bid); else memberConfirmations">
                                    <span class="unseal-status text-center">Rejected Evaluation</span>
                                </ng-container>
                                <ng-template #memberConfirmations>
                                    <span class="unseal-count">{{ getUnsealConfirmationText(bid) }}</span>
                                    <div class="unseal-member-statuses mt-2" *ngIf="!isBidUnsealedForScope(bid)">
                                        <div
                                            class="unseal-member-status"
                                            *ngFor="let memberStatus of getUnsealMemberStatuses(bid)"
                                            [class.confirmed]="memberStatus.hasConfirmed"
                                            [class.pending]="!memberStatus.hasConfirmed">
                                            <span class="member-name">{{ memberStatus.userName || 'Member' }}</span>
                                            <span class="member-role" *ngIf="memberStatus.role">{{ memberStatus.role }}</span>
                                            <span class="member-state">{{ getUnsealMemberStateText(bid, memberStatus) }}</span>
                                        </div>
                                    </div>
                                </ng-template>
                            </td>
                            <td class="text-end">
                                <button type="button" class="btn btn-sm broadcast-btn" (click)="secretaryUnsealBid(bid)" [disabled]="!canSecretaryUnsealBid(bid)">
                                    {{ getSecretaryUnsealButtonText(bid) }}
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div class="form-section" *ngIf="isEvaluationReviewStage">
            <h5 class="section-title fw-bold">Evaluation Review</h5>
            <div class="table-responsive mb-3" *ngIf="!isLoadingUnsealBids && unsealBids.length">
                <table class="table linked-evaluations-table mb-0"><thead><tr><th>Bid ID</th><th>Vendor</th><th>Amount</th><th>Documents</th><th>Evaluations</th></tr></thead><tbody>
                    <tr *ngFor="let bid of unsealBids"><td class="fw-bold">{{ bid.bidId }}</td><td>{{ bid.vendorName || '-' }}</td><td>{{ formatBidAmount(bid.bidAmount) }}</td><td><button type="button" class="btn btn-sm btn-outline-secondary" (click)="openBidDocumentsDialog(bid)">Documents</button></td><td><div *ngFor="let evaluation of bid.evaluations || []">{{ evaluation.evaluationType }}: {{ evaluation.decision || evaluation.status || '-' }}</div></td></tr>
                </tbody></table>
            </div>
            <div *ngIf="isLoadingEvaluationReviews" class="text-muted p-3 bg-light rounded"><i class="fas fa-spinner fa-spin me-2"></i>Loading evaluations...</div>
            <div *ngIf="!isLoadingEvaluationReviews && evaluationReviewItems.length === 0" class="text-muted p-3 bg-light rounded">No evaluations found for this agenda PR.</div>
            <div class="table-responsive" *ngIf="!isLoadingEvaluationReviews && evaluationReviewItems.length">
                <table class="table linked-evaluations-table mb-0">
                    <thead><tr><th>Evaluation ID</th><th>Type</th><th>Evaluator(s)</th><th>Status</th><th>Report</th><th>Evaluator Comments</th><th>Action</th><th *ngIf="hasSecretaryChangeRequest()">Secretary Comments</th><th>Review</th></tr></thead>
                    <tbody>
                        <tr *ngFor="let evaluation of evaluationReviewItems">
                            <td class="fw-bold">{{ evaluation.evaluationId }}</td>
                            <td>{{ evaluation.evaluationType || '-' }}</td>
                            <td>{{ evaluation.assignedTo || '-' }}</td>
                            <td><span class="evaluation-status">{{ evaluation.status }}</span></td>
                            <td>
                                <div *ngFor="let report of evaluation.reports || []">
                                    <button type="button" class="btn btn-sm btn-outline-secondary" (click)="downloadEvaluationReport(report)">Evaluation Report</button>
                                </div>
                                <span *ngIf="!(evaluation.reports || []).length" class="text-muted">No report uploaded</span>
                            </td>
                            <td><button type="button" class="btn btn-sm btn-outline-secondary" (click)="showEvaluatorComments(evaluation)">Evaluator Comments</button></td>
                            <td>{{ getEvaluationReviewActionText(evaluation) }}</td>
                            <td *ngIf="hasSecretaryChangeRequest()">{{ getEvaluationReviewActionText(evaluation) === 'Change Request' ? getEvaluationReviewComments(evaluation) : '-' }}</td>
                            <td>
                                <div *ngIf="canReviewAgendaEvaluation(evaluation); else reviewComplete" class="d-flex gap-2">
                                    <button type="button" class="btn btn-sm btn-outline-danger" [disabled]="reviewingEvaluationId === evaluation.evaluationId" (click)="reviewAgendaEvaluation(evaluation, false)">Request Changes</button>
                                    <button type="button" class="btn btn-sm broadcast-btn" [disabled]="reviewingEvaluationId === evaluation.evaluationId" (click)="reviewAgendaEvaluation(evaluation, true)">Accept</button>
                                </div>
                                <ng-template #reviewComplete><span class="text-muted">{{ getEvaluationReviewActionText(evaluation) }}</span></ng-template>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div class="form-section" *ngIf="shouldShowBidContext && !isEvaluationReviewStage">
            <h5 class="section-title fw-bold">Related Evaluations</h5>
            <div class="table-responsive" *ngIf="!isLoadingEvaluationReviews && evaluationReviewItems.length">
                <table class="table linked-evaluations-table mb-0"><thead><tr><th>Evaluation ID</th><th>Type</th><th>Evaluator(s)</th><th>Status</th><th>Report</th><th>Evaluator Comments</th><th>Action</th><th *ngIf="hasSecretaryChangeRequest()">Secretary Comments</th></tr></thead><tbody>
                    <tr *ngFor="let evaluation of evaluationReviewItems"><td class="fw-bold">{{ evaluation.evaluationId }}</td><td>{{ evaluation.evaluationType || '-' }}</td><td>{{ evaluation.assignedTo || '-' }}</td><td>{{ evaluation.status || '-' }}</td><td><div *ngFor="let report of evaluation.reports || []"><button type="button" class="btn btn-sm btn-outline-secondary" (click)="downloadEvaluationReport(report)">Evaluation Report</button></div><span *ngIf="!(evaluation.reports || []).length" class="text-muted">No report uploaded</span></td><td><button type="button" class="btn btn-sm btn-outline-secondary" (click)="showEvaluatorComments(evaluation)">Evaluator Comments</button></td><td>{{ getEvaluationReviewActionText(evaluation) }}</td><td *ngIf="hasSecretaryChangeRequest()">{{ getEvaluationReviewActionText(evaluation) === 'Change Request' ? getEvaluationReviewComments(evaluation) : '-' }}</td></tr>
                </tbody></table>
            </div>
            <div *ngIf="!isLoadingEvaluationReviews && !evaluationReviewItems.length" class="text-muted">No related evaluations found.</div>
        </div>

        <div class="form-section" *ngIf="isAwardBidStage">
            <h5 class="section-title fw-bold">Tender Committee Award Decision</h5>
            <div *ngIf="isLoadingAwardBids" class="text-muted p-3 bg-light rounded"><i class="fas fa-spinner fa-spin me-2"></i>Loading bids and evaluations...</div>
            <div *ngIf="!isLoadingAwardBids && awardBids.length === 0" class="text-muted p-3 bg-light rounded">No related bids found for this agenda PR.</div>
            <div class="table-responsive" *ngIf="!isLoadingAwardBids && awardBids.length">
                <table class="table linked-evaluations-table mb-0">
                    <thead><tr><th>Bid ID</th><th>Vendor</th><th>Amount</th><th>Valid Till</th><th>Evaluation Result</th><th>Documents</th><th>Committee Status</th><th>Action</th></tr></thead>
                    <tbody>
                        <tr *ngFor="let bid of awardBids">
                            <td class="fw-bold">{{ bid.bidId }}</td><td>{{ bid.vendorName || '-' }}</td><td>{{ formatBidAmount(bid.bidAmount) }}</td><td>{{ formatBidDate(bid.bidValidTill) }}</td>
                            <td>{{ bid.evaluations?.[0]?.decision || bid.evaluations?.[0]?.status || '-' }}</td>
                            <td><button type="button" class="btn btn-sm btn-outline-secondary" (click)="openBidDocumentsDialog(bid)"><i class="fas fa-folder-open me-1"></i>Documents</button></td>
                            <td><span class="evaluation-status">{{ getCommitteeBidStatus(bid) }}</span></td>
                            <td><button *ngIf="isCurrentUserSecretary()" type="button" class="btn btn-sm broadcast-btn" (click)="selectCommitteeWinner(bid)">{{ getCommitteeBidStatus(bid) === 'Awarded' ? 'Selected Winner' : 'Select Winner' }}</button></td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div class="form-section evaluator-assignment-section" *ngIf="shouldShowEvaluatorAssignmentSection">
            <div class="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                <div>
                    <h5 class="section-title fw-bold mb-1">Evaluator Assignment</h5>
                    <div class="text-muted small">Linked PR: <strong>{{ currentAgendaPrNumber || '-' }}</strong></div>
                </div>
                <button type="button" class="btn btn-sm broadcast-btn" (click)="openAssignEvaluatorsFromMeeting()" [disabled]="!currentAgendaPrNumber">
                    Assign Evaluators
                </button>
            </div>

            <div class="linked-evaluations mt-4">
                <h6 class="fw-bold mb-2">Linked Evaluations</h6>
                <div class="text-muted small" *ngIf="isLoadingLinkedEvaluations">Loading linked evaluations...</div>
                <div class="table-responsive" *ngIf="!isLoadingLinkedEvaluations && linkedAgendaEvaluations.length > 0">
                    <table class="table linked-evaluations-table mb-0">
                        <thead>
                            <tr>
                                <th>Evaluation ID</th>
                                <th>Type</th>
                                <th>Assigned To</th>
                                <th>Status</th>
                                <th>Due Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr *ngFor="let evaluation of linkedAgendaEvaluations">
                                <td class="fw-bold">{{ evaluation.evaluationId || evaluation.EvaluationId || '-' }}</td>
                                <td>{{ evaluation.evaluationType || evaluation.EvaluationType || '-' }}</td>
                                <td>{{ getAssignedEvaluatorNames(evaluation) }}</td>
                                <td>
                                    <span class="evaluation-status">{{ evaluation.status || evaluation.Status || '-' }}</span>
                                </td>
                                <td>{{ formatBidDate(evaluation.dueDate || evaluation.DueDate) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="alert alert-light border mb-0" *ngIf="!isLoadingLinkedEvaluations && linkedAgendaEvaluations.length === 0">
                    No evaluations created yet for this agenda PR.
                </div>
            </div>
        </div>

        <!-- TC Decision Section -->
        <div class="form-section">
            <h5 class="section-title fw-bold">TC Decision</h5>
            <div class="row">
                <div class="col-12 col-md-6 mb-3 mb-md-0">
                    <div class="form-group">
                        <label class="form-label fw-bold mb-2">TC Decision EN</label>
                        <div class="editor-with-voice">
                            <div class="rich-text-editor" [class.has-new-text]="hasNewVoiceText">
                                <div class="editor-toolbar">
                                    <button type="button" class="toolbar-btn" (click)="executeEditorCommand(tcDecisionEnEditor, 'tcDecisionEn', 'bold')" aria-label="Bold">
                                        <i class="fas fa-bold"></i>
                                    </button>
                                    <button type="button" class="toolbar-btn" (click)="executeEditorCommand(tcDecisionEnEditor, 'tcDecisionEn', 'italic')" aria-label="Italic">
                                        <i class="fas fa-italic"></i>
                                    </button>
                                    <button type="button" class="toolbar-btn" (click)="executeEditorCommand(tcDecisionEnEditor, 'tcDecisionEn', 'underline')" aria-label="Underline">
                                        <i class="fas fa-underline"></i>
                                    </button>
                                    <button type="button" class="toolbar-btn" (click)="executeEditorCommand(tcDecisionEnEditor, 'tcDecisionEn', 'insertOrderedList')" aria-label="Numbered List">
                                        <i class="fas fa-list-ol"></i>
                                    </button>
                                    <button type="button" class="toolbar-btn" (click)="executeEditorCommand(tcDecisionEnEditor, 'tcDecisionEn', 'insertUnorderedList')" aria-label="Bullet List">
                                        <i class="fas fa-list-ul"></i>
                                    </button>
                                    <label class="toolbar-btn toolbar-color-btn" aria-label="Text Color" title="Text Color">
                                        <i class="fas fa-font"></i>
                                        <span class="color-underline"></span>
                                        <input type="color" value="#532E60" (input)="executeEditorCommand(tcDecisionEnEditor, 'tcDecisionEn', 'foreColor', $any($event.target).value)">
                                    </label>
                                    <button type="button" class="toolbar-btn toolbar-highlight-btn" (click)="executeEditorCommand(tcDecisionEnEditor, 'tcDecisionEn', 'hiliteColor', '#FFF3B0')" aria-label="Highlight" title="Highlight">
                                        <span class="highlight-icon">A</span>
                                        <span class="highlight-preview"></span>
                                    </button>
                                    <button type="button" class="toolbar-btn" (click)="executeEditorCommand(tcDecisionEnEditor, 'tcDecisionEn', 'removeFormat')" aria-label="Clear Formatting" title="Clear Formatting">
                                        <span class="clear-format-icon">Tx</span>
                                    </button>
                                </div>
                                <div
                                    #tcDecisionEnEditor
                                    class="editor-surface"
                                    contenteditable="true"
                                    [attr.data-placeholder]="'Enter TC Decision in English...'"
                                    [class.is-empty]="isEditorEmpty('tcDecisionEn')"
                                    (input)="onEditorInput('tcDecisionEn', tcDecisionEnEditor)"
                                    (paste)="onEditorPaste($event, 'tcDecisionEn', tcDecisionEnEditor)"
                                    (blur)="onEditorBlur('tcDecisionEn')"></div>
                            </div>
                            <button 
                                type="button" 
                                class="voice-btn"
                                (click)="toggleVoiceInput()"
                                [class.recording]="isRecording"
                                [disabled]="!isVoiceSupported"
                                [title]="isRecording ? 'Stop recording' : 'Start voice input'">
                                <i class="fas" [class.fa-microphone]="!isRecording" [class.fa-stop-circle]="isRecording"></i>
                            </button>
                            <!-- New voice text indicator -->
                            <div class="new-voice-text-indicator" *ngIf="currentVoiceTranscript">
                                <i class="fas fa-microphone me-2"></i>
                                <span>{{ currentVoiceTranscript }}</span>
                            </div>
                        </div>
                        <div class="mt-2 d-flex gap-2">
                            <button 
                                type="button" 
                                class="btn btn-sm translate-btn"
                                (click)="onTranslateToArabic()"
                                [disabled]="!hasEditorText('tcDecisionEn') || isTranslating">
                                <span *ngIf="!isTranslating">🌐 Translate to Arabic</span>
                                <span *ngIf="isTranslating">⏳ Translating...</span>
                            </button>
                            <button 
                                type="button" 
                                class="btn btn-sm broadcast-btn"
                                (click)="broadcastTCDecision()"
                                [disabled]="!hasEditorText('tcDecisionEn') && !hasEditorText('tcDecisionAr')">
                                <span>Broadcast Decision</span>
                            </button>
                        </div>
                    </div>
                </div>
                <div class="col-12 col-md-6">
                    <div class="form-group">
                        <label class="form-label fw-bold mb-2">TC Decision AR</label>
                        <div class="editor-with-voice">
                            <div class="rich-text-editor arabic-editor" [class.has-new-text]="hasNewVoiceTextAr">
                                <div class="editor-toolbar">
                                    <button type="button" class="toolbar-btn" (click)="executeEditorCommand(tcDecisionArEditor, 'tcDecisionAr', 'bold')" aria-label="Bold">
                                        <i class="fas fa-bold"></i>
                                    </button>
                                    <button type="button" class="toolbar-btn" (click)="executeEditorCommand(tcDecisionArEditor, 'tcDecisionAr', 'italic')" aria-label="Italic">
                                        <i class="fas fa-italic"></i>
                                    </button>
                                    <button type="button" class="toolbar-btn" (click)="executeEditorCommand(tcDecisionArEditor, 'tcDecisionAr', 'underline')" aria-label="Underline">
                                        <i class="fas fa-underline"></i>
                                    </button>
                                    <button type="button" class="toolbar-btn" (click)="executeEditorCommand(tcDecisionArEditor, 'tcDecisionAr', 'insertOrderedList')" aria-label="Numbered List">
                                        <i class="fas fa-list-ol"></i>
                                    </button>
                                    <button type="button" class="toolbar-btn" (click)="executeEditorCommand(tcDecisionArEditor, 'tcDecisionAr', 'insertUnorderedList')" aria-label="Bullet List">
                                        <i class="fas fa-list-ul"></i>
                                    </button>
                                    <label class="toolbar-btn toolbar-color-btn" aria-label="Text Color" title="Text Color">
                                        <i class="fas fa-font"></i>
                                        <span class="color-underline"></span>
                                        <input type="color" value="#532E60" (input)="executeEditorCommand(tcDecisionArEditor, 'tcDecisionAr', 'foreColor', $any($event.target).value)">
                                    </label>
                                    <button type="button" class="toolbar-btn toolbar-highlight-btn" (click)="executeEditorCommand(tcDecisionArEditor, 'tcDecisionAr', 'hiliteColor', '#FFF3B0')" aria-label="Highlight" title="Highlight">
                                        <span class="highlight-icon">A</span>
                                        <span class="highlight-preview"></span>
                                    </button>
                                    <button type="button" class="toolbar-btn" (click)="executeEditorCommand(tcDecisionArEditor, 'tcDecisionAr', 'removeFormat')" aria-label="Clear Formatting" title="Clear Formatting">
                                        <span class="clear-format-icon">Tx</span>
                                    </button>
                                </div>
                                <div
                                    #tcDecisionArEditor
                                    class="editor-surface"
                                    contenteditable="true"
                                    [attr.data-placeholder]="'Enter TC Decision in Arabic...'"
                                    [class.is-empty]="isEditorEmpty('tcDecisionAr')"
                                    (input)="onEditorInput('tcDecisionAr', tcDecisionArEditor)"
                                    (paste)="onEditorPaste($event, 'tcDecisionAr', tcDecisionArEditor)"
                                    (blur)="onEditorBlur('tcDecisionAr')"></div>
                            </div>
                            <textarea 
                                class="form-control d-none" 
                                rows="4"
                                formControlName="tcDecisionAr"
                                [class.has-new-text]="hasNewVoiceTextAr"
                                placeholder="أدخل قرار اللجنة بالعربية...">
                            </textarea>
                            <button 
                                type="button" 
                                class="voice-btn"
                                (click)="toggleVoiceInputArabic()"
                                [class.recording]="isRecordingArabic"
                                [disabled]="!isVoiceSupported"
                                [title]="isRecordingArabic ? 'إيقاف التسجيل' : 'بدء إدخال الصوت'">
                                <i class="fas" [class.fa-microphone]="!isRecordingArabic" [class.fa-stop-circle]="isRecordingArabic"></i>
                            </button>
                            <!-- New voice text indicator for Arabic -->
                            <div class="new-voice-text-indicator" *ngIf="currentVoiceTranscriptAr">
                                <i class="fas fa-microphone me-2"></i>
                                <span>{{ currentVoiceTranscriptAr }}</span>
                            </div>
                        </div>
                        <div class="mt-2 d-flex gap-2">
                            <button 
                                type="button" 
                                class="btn btn-sm translate-btn"
                                (click)="onTranslateToEnglish()"
                                [disabled]="!hasEditorText('tcDecisionAr') || isTranslating">
                                <span *ngIf="!isTranslating">🌐 Translate to English</span>
                                <span *ngIf="isTranslating">⏳ Translating...</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </div>

        <!-- Dialog Footer -->
        <div class="dialog-footer d-flex justify-content-between align-items-center">
            <div>
                <button 
                    type="button" 
                    class="btn btn-secondary me-2" 
                    (click)="goToPrevious()"
                    [disabled]="!hasPrevious">
                    <i class="fas fa-arrow-left me-2"></i> Back
                </button>
                <button 
                    *ngIf="hasNext"
                    type="button" 
                    class="btn btn-primary" 
                    (click)="goToNext()"
                    [disabled]="!canMoveForwardFromCurrentAgenda"
                    [title]="!canMoveForwardFromCurrentAgenda ? getForwardNavigationBlockedMessage('navigate forward') : ''">
                    Next <i class="fas fa-arrow-right ms-2"></i>
                </button>
                <button 
                    *ngIf="!hasNext"
                    type="button" 
                    class="btn btn-danger" 
                    (click)="finishMeeting()"
                    [disabled]="!canMoveForwardFromCurrentAgenda"
                    [title]="!canMoveForwardFromCurrentAgenda ? getForwardNavigationBlockedMessage('finish meeting') : ''">
                    <i class="fas fa-check me-2"></i>Finish the Meeting
                </button>
            </div>
            <button
                type="button"
                class="btn btn-danger"
                (click)="finishMeeting()"
                [disabled]="!canMoveForwardFromCurrentAgenda"
                [title]="!canMoveForwardFromCurrentAgenda ? getForwardNavigationBlockedMessage('end meeting') : ''">
                <i class="fas fa-stop me-2"></i> End Meeting
            </button>
        </div>
    </form>
</div>

